import dayjs from "dayjs";
import { isAuthenticated } from "../../../commons/Auth/auth";
import { hasPermission } from "../../../commons/Permissions/PermissionsHook";

// Statutory figures the templates quote. Update them every January:
// Geringfügigkeitsgrenze (§ 8 Abs. 1a SGB IV, follows the Mindestlohn) and the upper end of
// the Übergangsbereich (§ 20 Abs. 2 SGB IV). Values for 2026.
export const MINIJOB_LIMIT = 603;
export const MIDIJOB_LIMIT = 2000;
// Werkstudentenprivileg: at most 20 hours a week during the semester.
export const WERKSTUDENT_MAX_HOURS = 20;

// The keys match the contractType enum on the server (utils/contracts/templates.js).
// `employment` types need one of the provider's employees as the counterparty;
// `alwaysFixedTerm` types need an end date.
export const CONTRACT_TYPES = [
  {
    key: "vollzeit",
    label: "Full-time",
    description: "Permanent or fixed-term full-time employment",
    employment: true
  },
  {
    key: "teilzeit",
    label: "Part-time",
    description: "Employment with reduced weekly hours",
    employment: true
  },
  {
    key: "minijob",
    label: "Mini job",
    description: "Marginal employment up to {{limit}} € a month",
    employment: true
  },
  {
    key: "midijob",
    label: "Midi job",
    description: "Earnings between {{min}} € and {{max}} € a month",
    employment: true
  },
  {
    key: "aushilfe",
    label: "Temporary help",
    description: "Short-term employment, at most 3 months or 70 working days a year",
    employment: true,
    alwaysFixedTerm: true
  },
  {
    key: "werkstudent",
    label: "Working student",
    description: "Enrolled students, at most 20 hours a week during the semester",
    employment: true
  },
  {
    key: "praktikum",
    label: "Internship",
    description: "Mandatory or voluntary internship",
    employment: true,
    alwaysFixedTerm: true
  },
  {
    key: "freelancer",
    label: "Freelancer",
    description: "Independent contractor, not an employee",
    employment: false
  },
  {
    key: "subunternehmer",
    label: "Subcontractor",
    description: "Contract for work with another company",
    employment: false
  },
  {
    key: "sonstiges",
    label: "Other contract",
    description: "Start from a short general contract",
    employment: false
  }
];

export const getContractType = key =>
  CONTRACT_TYPES.find(type => type.key === key) ||
  CONTRACT_TYPES.find(type => type.key === "sonstiges");

export const describeType = (type, t) =>
  t(type.description, { limit: MINIJOB_LIMIT, min: MINIJOB_LIMIT, max: MIDIJOB_LIMIT });

export const STATUS_COLORS = {
  draft: "default",
  partially_signed: "orange",
  signed: "green"
};
export const STATUS_LABELS = {
  draft: "Draft",
  partially_signed: "Partially signed",
  signed: "Signed"
};

/** What the logged-in user may do with contracts ("Create", "Read", "Update", "Delete"). */
export const canContracts = action => {
  const user = isAuthenticated();
  if (!user) return false;
  if (Number(user.role) === 2) return true;
  return hasPermission(user?.roleId?.permissions, "Contracts", action);
};

// Placeholders offered in the template editor, in the order they appear in most contracts.
export const PLACEHOLDERS = [
  "employerName",
  "employerAddress",
  "employerRepresentative",
  "partyName",
  "partyAddress",
  "partyBirthDate",
  "partyCompany",
  "partyTaxNumber",
  "startDate",
  "endDate",
  "jobTitle",
  "workplace",
  "weeklyHours",
  "salary",
  "salaryUnit",
  "vacationDays",
  "probationMonths",
  "noticePeriod",
  "minijobLimit",
  "midijobLimit",
  "today"
];

const BLANK = "__________";

const escapeHtml = value =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatDate = value => (value ? dayjs(value).format("DD.MM.YYYY") : "");

const formatMoney = value =>
  value === undefined || value === null || value === ""
    ? ""
    : Number(value).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const salaryUnit = (salaryType, employment) => {
  if (salaryType === "hourly") return employment ? "brutto pro Stunde" : "pro Stunde";
  if (salaryType === "flat") return "pauschal";
  return employment ? "brutto monatlich" : "monatlich";
};

/**
 * The values a template is filled with, from the form values and the employer details the
 * templates endpoint returns. Contract texts are German, so dates and amounts are too.
 */
export const buildTemplateVars = (values, employer, employee) => {
  const type = getContractType(values.contractType);
  const terms = values.terms || {};
  const fixedTerm = Boolean(type.alwaysFixedTerm || values.fixedTerm);
  const partyName = type.employment
    ? [employee?.firstName, employee?.lastName].filter(Boolean).join(" ")
    : values.partyName;
  return {
    employerName: employer?.name,
    employerAddress: employer?.address,
    employerRepresentative: employer?.representative,
    partyName,
    partyAddress: [values.address, [values.postalCode, values.city].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
    partyBirthDate: employee?.birthDate || employee?.dob,
    partyCompany: values.partyCompany,
    partyTaxNumber: values.partyTaxNumber,
    startDate: formatDate(values.startDate),
    endDate: fixedTerm ? formatDate(values.endDate) : "",
    jobTitle: terms.jobTitle,
    workplace: terms.workplace,
    weeklyHours: terms.weeklyHours,
    salary: formatMoney(terms.salary),
    salaryUnit: salaryUnit(terms.salaryType, type.employment),
    vacationDays: terms.vacationDays,
    probationMonths: terms.probationMonths,
    noticePeriod: terms.noticePeriod,
    minijobLimit: formatMoney(MINIJOB_LIMIT),
    midijobLimit: formatMoney(MIDIJOB_LIMIT),
    today: dayjs().format("DD.MM.YYYY"),
    // Section flags
    fixedTerm,
    probation: Number(terms.probationMonths) > 0
  };
};

/**
 * Fills a template: {{#flag}}…{{/flag}} and {{^flag}}…{{/flag}} sections first, then
 * {{name}} placeholders. Missing values become a blank line to fill in by hand, so a gap in
 * the form shows up in the text instead of disappearing.
 */
export const fillTemplate = (body, vars) => {
  let text = body || "";
  const section = /\{\{([#^])(\w+)\}\}([\s\S]*?)\{\{\/\2\}\}/g;
  // Loop so nested sections resolve too.
  for (let previous = null; previous !== text; ) {
    previous = text;
    text = text.replace(section, (_, kind, name, inner) => {
      const value = vars[name];
      const truthy = Array.isArray(value) ? value.length > 0 : Boolean(value);
      return (kind === "#") === truthy ? inner : "";
    });
  }
  return (
    text
      .replace(/\{\{(\w+)\}\}/g, (_, name) => {
        const value = vars[name];
        return value === undefined || value === null || value === "" ? BLANK : escapeHtml(value);
      })
      // Once a template has been through Quill, a section marker sits in a paragraph of its
      // own (<p>{{#flag}}</p>), and removing the section leaves an empty <p></p> behind. Quill's
      // own blank lines are <p><br></p> and stay.
      .replace(/<p>\s*<\/p>/g, "")
  );
};

// Quill setup for contract texts: no images (they would be stored inline) but alignment,
// which contracts use for headings.
export const contractQuillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["link", "clean"]
  ]
};
export const contractQuillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "indent",
  "align",
  "link"
];
